/** @jsxImportSource @emotion/react */
import { createContext, useContext } from 'react'
import { PlayerState } from './types'
import {
  usePlayerQuery,
  usePlayerHighlightsQuery,
} from '../../hooks/usePlayerData'

interface PlayerContextValue {
  state: PlayerState
  refetch: () => void
}

const PlayerContext = createContext<PlayerContextValue | null>(null)

interface PlayerProviderProps {
  playerId: string
  children: React.ReactNode
}

export function PlayerProvider({ playerId, children }: PlayerProviderProps) {
  const playerQuery = usePlayerQuery(playerId)
  const highlightsQuery = usePlayerHighlightsQuery(playerId)

  // Map the two cached queries onto the existing PlayerState shape so consumers
  // (StatsTab / OverviewTab / PlayerHeader / PlayerNavBar) stay unchanged.
  const state: PlayerState = {
    player: playerQuery.data ?? null,
    highlights: highlightsQuery.data ?? [],
    loading: playerQuery.isPending || highlightsQuery.isPending,
    error: playerQuery.error
      ? playerQuery.error instanceof Error
        ? playerQuery.error.message
        : 'Failed to load player'
      : null,
  }

  const refetch = () => {
    playerQuery.refetch()
    highlightsQuery.refetch()
  }

  const value: PlayerContextValue = { state, refetch }

  return (
    <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>
  )
}

export function usePlayerContext(): PlayerContextValue {
  const context = useContext(PlayerContext)
  if (!context) {
    throw new Error('usePlayerContext must be used within a PlayerProvider')
  }
  return context
}
